/**
 * Assignment Overview Tab Component
 * Shows assignment timeline, statistics, and quick actions
 */

import { Box, Typography, Button } from '@mui/material'
import { OpenInNew } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { CalendarIcon, AssignmentIcon } from 'src/v3/core/icons'

interface OverviewTabProps {
  assignment: any
  event: any
}

export default function OverviewTab({ assignment, event }: OverviewTabProps) {
  let navigate = useNavigate()

  let handleViewAssignment = () => {
    navigate(`/manage/assignments/${event.assignmentID}`)
  }

  // Calculate actual total CE hours from assignment courses
  let actualTotalCeHours =
    assignment?.courses?.reduce(
      (total: number, course: any) => total + (course.ceHours || 0),
      0,
    ) || 0

  return (
    <Box>
      {/* Assignment Timeline */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <CalendarIcon />
          Assignment Timeline
        </Typography>
        <Box className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2">Created</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {dayjs(assignment.createdTime).format('MMM D, YYYY')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(assignment.createdTime).fromNow()}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2">Due Date</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {assignment.dueDate
                ? dayjs(assignment.dueDate).format('MMM D, YYYY')
                : 'No due date'}
            </Typography>
            {assignment.dueDate && (
              <Typography variant="caption" color="text.secondary">
                {dayjs(assignment.dueDate).fromNow()}
              </Typography>
            )}
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2">Status</Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 0.5 }}
              color={assignment.isActive ? 'success.main' : 'text.secondary'}
            >
              {assignment.isActive ? 'Active' : 'Inactive'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {assignment.isActive
                ? 'Currently accepting submissions'
                : 'Closed'}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* Assignment Statistics */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <AssignmentIcon />
          Assignment Statistics
        </Typography>
        <Box className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Box
            sx={{
              textAlign: 'center',
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <Typography
              color="primary.main"
              variant="h4"
              sx={{
                fontWeight: 700,
              }}
            >
              {event.userCount}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Users
            </Typography>
          </Box>
          <Box
            sx={{
              textAlign: 'center',
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: 'info.main',
              }}
            >
              {event.courseCount}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Courses
            </Typography>
          </Box>
          <Box
            sx={{
              textAlign: 'center',
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <Typography
              color="success.main"
              variant="h4"
              sx={{
                fontWeight: 700,
              }}
            >
              {actualTotalCeHours}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total CE Hours
            </Typography>
          </Box>
          <Box
            sx={{
              textAlign: 'center',
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <Typography
              color="warning.main"
              variant="h4"
              sx={{
                fontWeight: 700,
              }}
            >
              {Math.round(event.overallCompletionPercentage)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Completion Rate
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* Assignment Description */}
      {assignment.description && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Description
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {assignment.description}
          </Typography>
        </Box>
      )}
      {/* Quick Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<OpenInNew />}
          onClick={handleViewAssignment}
        >
          View Full Assignment
        </Button>
      </Box>
    </Box>
  )
}
